import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { UserInterface } from './type';
import { UserRepository } from './repository';
import { useOrders } from '@/features/orders/controller'
import { useStripeTerminal } from '@/features/stripeTerminal/controller'
import { useToast } from 'primevue/usetoast'

export const useUsers = defineStore('users', () => {

    const userRepository = new UserRepository();
    const loggedUser = ref<UserInterface | null>(null)
    const selectedUserForShop = ref<any>(null)
    const $ordersStore = useOrders();
    const toast = useToast()

    const $stripeTerminalStore = useStripeTerminal();

    const isProfessional = computed(() =>
        !loggedUser.value ? false :
            ['professional'/*, 'administrator', 'storekeeper'*/].includes(loggedUser.value?.type || '')
    );

    const isStorekeeper = computed(() =>
        !loggedUser.value ? false :
            ['administrator', 'storekeeper'].includes(loggedUser.value?.type || '')
    );

    const loginAdmin = async (credentials: { email: string, password: string }) => {
        const res = await userRepository.loginAdmin(credentials)
        if (!res.error) {
            loggedUser.value = res.data.data.user
        }
        return res
    }

    const logoutAdmin = async () => {
        const res = await userRepository.logoutAdmin();
        if (!res.error) {
            loggedUser.value = null;
            $ordersStore.activeOrder = null;
            window.location.href = '/connexion';
        }
    }

    const login = async (credentials: { email: string, password: string }) => {
        if ($stripeTerminalStore.paymentIntentIdStore) {
            await $stripeTerminalStore.cancelPayment($stripeTerminalStore.paymentIntentIdStore);
        } else {
            await $stripeTerminalStore.cancelTerminal();
        }
        const res = await userRepository.login(credentials)
        if (!res.error) {
            loggedUser.value = res.data.data
        }
        return res
    }

    const logout = async () => {
        // const res2 = await userRepository.clearOrders();
        const res = await userRepository.logout();
        if (!res.error) {
            await $stripeTerminalStore.cancelTerminal();
            loggedUser.value = null;
            $ordersStore.activeOrder = null;
            window.location.href = '/';
        }
    }
    const getUserAdmin = async () => {
        const res = await userRepository.getUserAdmin()
        if (!res.error) {
            loggedUser.value = res.data.user
        }
        return res
    }

    const getUser = async () => {
        const res = await userRepository.getUser()
        if (!res.error) {
            loggedUser.value = res.data.user
        }
        return res
    }

    const register = async (userData: object) => {
        const res = await userRepository.register(userData)
        if (!res.error && res.data?.data?.user) {
            loggedUser.value = res.data.data.user
        }
        return res
    }

    const forgotPassword = async (email: string) => {
        const res = await userRepository.forgotPassword(email)
        return res
    }

    const resetPassword = async (resetData: { token: string; email: string; password: string; password_confirmation: string }) => {
        const res = await userRepository.resetPassword(resetData)
        return res
    }

    const forgotPassword2 = async (email: string) => {
        const res = await userRepository.forgotPassword2(email)
        return res
    }

    const resetPassword2 = async (resetData: { token: string; email: string; password: string; password_confirmation: string }) => {
        const res = await userRepository.resetPassword2(resetData)
        return res
    }


    const getCompanies = async () => {
        const res = await userRepository.getCompanies()
        return res
    }

    const getCompanyUsers = async (companyId: number) => {
        const res = await userRepository.getCompanyUsers(companyId)
        return res
    }

    const requestProfessionalInfo = async (email: string) => {
        const res = await userRepository.requestProfessionalInfo(email)
        return res
    }

    const activateUserAccount = async (activationData: { token: string; email: string; password: string; password_confirmation: string; first_name: string; last_name: string }) => {
        const res = await userRepository.activateUserAccount(activationData)
        return res
    }

    const activateAdminAccount = async (activationData: { token: string; email: string; password: string; password_confirmation: string; first_name: string; last_name: string }) => {
        const res = await userRepository.activateAdminAccount(activationData)
        return res
    }

    const getGlobalParams = async (keys: string[]) => {
        const res = await userRepository.getGlobalParams(keys)
        return res
    }

    const sendAdviceRequest = async (adviceData: { product_id: number; product_name: string; product_reference: string; message?: string }) => {
        const res = await userRepository.sendAdviceRequest(adviceData)
        if (!res.error) {
            toast.add({
                severity: 'success',
                summary: 'Demande envoyée',
                detail: 'Votre demande de conseil a été envoyée avec succès. Un conseiller vous contactera bientôt.',
                life: 4000
            });
        } else {
            toast.add({
                severity: 'error',
                summary: 'Erreur',
                detail: 'Une erreur s\'est produite lors de l\'envoi de votre demande.',
                life: 4000
            });
        }
        return res
    }

    const getAllUsers = async () => {
        const res = await userRepository.getAllUsers()
        return res
    }

    const checkBadge = async (uid: string) => {
        const res = await userRepository.checkBadge(uid)
        return res
    }

    const loginWithBadge = async (uid: string, pin: string) => {
        // Annuler les paiements en cours si nécessaire
        if ($stripeTerminalStore.paymentIntentIdStore) {
            await $stripeTerminalStore.cancelPayment($stripeTerminalStore.paymentIntentIdStore);
        } else {
            await $stripeTerminalStore.cancelTerminal();
        }

        const res = await userRepository.loginWithBadge(uid, pin)
        if (!res.error) {
            loggedUser.value = res.data.data
        }
        return res
    }

    return {
        getUserAdmin,

        isProfessional,
        isStorekeeper,
        loggedUser,
        selectedUserForShop,
        loginAdmin,
        logoutAdmin,
        login,
        logout,
        register,
        getUser,
        forgotPassword,
        resetPassword,
        forgotPassword2,
        resetPassword2,
        getCompanies,
        getCompanyUsers,
        requestProfessionalInfo,
        activateUserAccount,
        activateAdminAccount,
        getGlobalParams,
        sendAdviceRequest,
        getAllUsers,
        checkBadge,
        loginWithBadge,
    }

}, {
    persist: true,
})
